import './style.scss';

import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import { useAccount, useChainId } from 'wagmi';

import { useThemeColors } from '@/shared/theme';

import { ApiWithdrawWhitelist } from 'api';
import { IWithdrawWhitelistItem } from 'api/apiWithdrawWhitelist/models';
import { useSignWithdrawWalletApprove, useWithdrawalWhitelist } from 'hooks/dex';
import { AddWithdrawWhitelistModal } from 'modals';
import { RELOAD_WITHDRAWAL_WHTELIST } from 'shared/consts/reload';
import { useTypedDispatch, useTypedSelector } from 'store';
import { setCopyPopupShow } from 'store/slices/ui';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { StaticTable, TBody } from 'tables/common';
import { Bus } from 'tools';
import { EmptyIcon } from 'ui/Alert/icons';
import { Button } from 'ui/Button/Button';
import { ButtonLoading } from 'ui/Button/ButtonLoading';
import { ChangePairNetwork } from 'ui/ChangePairNetwork/ChangePairNetwork';
import { Modal } from 'ui/Modal/Modal';
import { Spinner } from 'ui/Spinner/Spinner';
import { chains } from 'web3';

import { WalletCell } from './WalletCell';

import { CloseIcon } from 'assets/icons';
import { CopyIcon } from 'assets/icons/CopyIcon';

interface IWithdrawWhitelistModalProps {
  onClose: () => void;
}

const WithdrawWhitelistModal: React.FC<IWithdrawWhitelistModalProps> = ({ onClose }) => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const userId = useTypedSelector(store => store.auth.user?.id);
  const palette = useThemeColors();
  const { address } = useAccount();
  const chainId = useChainId();
  const dispatch = useTypedDispatch();

  const {
    loading,
    totalItems,
    whitelist,
    setWhitelist,
    load: handleLoadWhitelist,
  } = useWithdrawalWhitelist();

  const [addWhitelistModalOpened, setAddWhitelistModalOpened] = useState<boolean>(false);
  const [invalidMetamaskModalOpened, setInvalidMetamaskModalOpened] = useState<boolean>(false);

  const handleCopyAllWhitelist = useCallback(() => {
    const preparedString = whitelist.map(el => el.wallet_address).join('\n');
    navigator.clipboard.writeText(preparedString);
    dispatch(setCopyPopupShow(true));
  }, [whitelist, dispatch]);

  const handleCopySingleWallet = useCallback(
    (wallet: string) => {
      navigator.clipboard.writeText(wallet);
      dispatch(setCopyPopupShow(true));
    },
    [dispatch],
  );

  const handleDeleteWalletFromWhitelist = useCallback(
    async (id: number) => {
      try {
        const { isSuccess, errorMessage } = await ApiWithdrawWhitelist.deleteWithdrawWhitelist({
          id,
        });

        if (isSuccess) {
          Bus.emit(RELOAD_WITHDRAWAL_WHTELIST);
          setWhitelist(v => v.filter(el => el.id !== id));
        } else {
          dispatch(
            setAlertState({
              type: 'failed-img',
              text: errorMessage ?? 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => dispatch(dropAlertState()),
            }),
          );
        }
      } catch (error) {
        console.log('error: ', error);
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => dispatch(dropAlertState()),
          }),
        );
      }
    },
    [dispatch, setWhitelist],
  );

  const showDeletingSureModal = useCallback(
    (id: number) => {
      dispatch(
        setAlertState({
          type: 'sure',
          text: 'Are you sure that you want to delete wallet from whitelist?',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
            handleDeleteWalletFromWhitelist(id);
          },
        }),
      );
    },
    [dispatch, handleDeleteWalletFromWhitelist],
  );

  const onSignError = useCallback(
    (message: string) => {
      dispatch(
        setAlertState({
          type: 'failed',
          text: message,
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
          },
        }),
      );
    },
    [dispatch],
  );

  const onSignSuccess = useCallback(
    async (walletId: number) => {
      try {
        const {
          isSuccess,
          errorMessage,
          data: updatedWallet,
        } = await ApiWithdrawWhitelist.getWithdrawWhitelistItem({ id: walletId });

        if (isSuccess && updatedWallet) {
          Bus.emit(RELOAD_WITHDRAWAL_WHTELIST);
          setWhitelist(v => {
            const newWhitelist = [...v];
            const findInex = newWhitelist.findIndex(el => el.id === walletId);

            newWhitelist[findInex] = updatedWallet;

            return newWhitelist;
          });
        } else if (errorMessage) {
          dispatch(
            setAlertState({
              type: 'failed',
              text: 'Something went wrong',
              onClose: () => dispatch(dropAlertState()),
              onSubmit: () => {
                dispatch(dropAlertState());
              },
            }),
          );
        }
      } catch (error) {
        console.log('error: ', error);
        dispatch(
          setAlertState({
            type: 'failed',
            text: 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      }
    },
    [dispatch, setWhitelist],
  );

  const signMessage = useSignWithdrawWalletApprove({
    onError: onSignError,
    onSuccess: onSignSuccess,
  });

  const metamaskConnectionIsValid = useMemo<boolean>(() => {
    if (!address) return false;

    for (const network of Object.keys(chains)) {
      if (chainId && dexPair.network === network && chainId !== chains[network]) return false;
    }

    return true;
  }, [address, dexPair, chainId]);

  const handleAccept = useCallback(
    async (id: number) => {
      if (!metamaskConnectionIsValid) {
        return setInvalidMetamaskModalOpened(true);
      }

      signMessage({ id });
    },
    [signMessage, metamaskConnectionIsValid],
  );

  const showAcceptSureModal = useCallback(
    (wallet: IWithdrawWhitelistItem) => {
      dispatch(
        setAlertState({
          type: 'sure',
          text: `Are you sure that you want to accept ${wallet.wallet_address} wallet to whitelist?`,
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
            handleAccept(wallet.id);
          },
        }),
      );
    },
    [dispatch, handleAccept],
  );

  const checkCanAccept = useCallback(
    (wallet: IWithdrawWhitelistItem) =>
      wallet.can_confirm &&
      (!wallet.confirmations || !wallet.confirmations.find(el => el.user.id === userId)),
    [userId],
  );

  const columns = useMemo<ColumnDef<IWithdrawWhitelistItem>[]>(
    () => [
      {
        id: 'wallets',
        accessorFn: row => row,
        header: () => <div style={{ textAlign: 'left' }}>Wallets: {totalItems}</div>,
        cell: ({ row }) => <WalletCell wallet={row.original} />,
      },
      {
        id: 'actions',
        accessorFn: row => row,
        header: () => (
          <motion.div
            onClick={handleCopyAllWhitelist}
            style={{
              textAlign: 'right',
              color: palette.primary,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Copy all
          </motion.div>
        ),
        cell: ({ row }) => {
          return (
            <div className="table-actions">
              {checkCanAccept(row.original) && (
                <div className="accept-action" onClick={() => showAcceptSureModal(row.original)}>
                  Accept
                </div>
              )}
              <motion.div
                className="copy-icon"
                onClick={() => handleCopySingleWallet(row.original.wallet_address)}
              >
                <CopyIcon />
              </motion.div>
              <motion.div
                className="close-icon"
                onClick={() => showDeletingSureModal(row.original.id)}
              >
                <CloseIcon />
              </motion.div>
            </div>
          );
        },
      },
    ],
    [
      totalItems,
      handleCopySingleWallet,
      handleCopyAllWhitelist,
      showDeletingSureModal,
      showAcceptSureModal,
      checkCanAccept,
      palette,
    ],
  );

  const table = useReactTable({
    columns,
    data: whitelist,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();

  return (
    <>
      {!addWhitelistModalOpened && (
        <Modal onClose={onClose} title="Withdrawal whitelist">
          <div className="mm-withdraw-whitelist-modal">
            {loading && <Spinner size="small" />}

            {!loading && whitelist.length === 0 && (
              <div className="whitelist-empty">
                <EmptyIcon />
                <span>Your whitelist is empty</span>
              </div>
            )}

            {!loading && whitelist.length !== 0 && (
              <StaticTable className="mm-common-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{ width: header.getSize() }}
                        >
                          {header.isPlaceholder ? null : (
                            <div>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <TBody>
                  {rows.map(row => (
                    <React.Fragment key={row.index}>
                      <tr>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  ))}
                </TBody>
              </StaticTable>
            )}

            <div className="whitelist-actions">
              {loading && <ButtonLoading />}
              {!loading && (
                <Button onClick={() => setAddWhitelistModalOpened(true)}>Add wallet</Button>
              )}
            </div>
          </div>
        </Modal>
      )}
      {addWhitelistModalOpened && (
        <AddWithdrawWhitelistModal
          onClose={() => setAddWhitelistModalOpened(false)}
          onConfirm={() => handleLoadWhitelist()}
        />
      )}
      {invalidMetamaskModalOpened && (
        <ChangePairNetwork onClose={() => setInvalidMetamaskModalOpened(false)} />
      )}
    </>
  );
};

export { WithdrawWhitelistModal };
